import React, { useMemo } from 'react';
import { BooleanInput, FormDataConsumer, TextInput, useGetOne } from 'react-admin';
import { Box, Grid, Paper, Typography, alpha } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import StarIcon from '@mui/icons-material/Star';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PersonIcon from '@mui/icons-material/Person';
import { useController } from 'react-hook-form';
import { getChainId } from '../../../lib';

const TYPE_CONFIG = {
  default: {
    icon: StorefrontIcon,
    label: 'Padrão',
    description: 'Desconto geral vinculado a postos. Disponível para qualquer cliente que abastecer nos postos selecionados.',
    color: '#607d8b',
  },
  special: {
    icon: StarIcon,
    label: 'Especial',
    description: 'Desconto vinculado a postos e segmentado por grupos, tags ou cidades. Suporta agendamento por período e envio de notificações.',
    color: '#1976d2',
  },
  reward: {
    icon: CardGiftcardIcon,
    label: 'Recompensa',
    description: 'Desconto vinculado ao Clube de Vantagens. Concedido ao cliente como prêmio ao resgatar um presente.',
    color: '#7b1fa2',
  },
  recovery: {
    icon: PersonIcon,
    label: 'Recuperação',
    description: 'Desconto vinculado à regra de recuperação de clientes inativos. Cada cliente recebe seu próprio desconto conforme o nível em que se encontra.',
    color: '#e65100',
  },
};

interface TypeCardProps {
  value: string;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}

const TypeCard: React.FC<TypeCardProps> = ({ value, selected, disabled, onClick }) => {
  const config = TYPE_CONFIG[value as keyof typeof TYPE_CONFIG];
  if (!config) return null;

  const Icon = config.icon;

  return (
    <Paper
      onClick={disabled ? undefined : onClick}
      elevation={selected ? 3 : 0}
      sx={{
        p: 2.5,
        cursor: disabled ? 'default' : 'pointer',
        border: '2px solid',
        borderColor: selected ? config.color : 'divider',
        borderRadius: 2,
        opacity: disabled ? 0.4 : 1,
        transition: 'all 0.18s ease',
        background: selected
          ? alpha(config.color, 0.06)
          : 'background.paper',
        '&:hover': disabled ? {} : {
          borderColor: config.color,
          background: alpha(config.color, 0.04),
          transform: 'translateY(-1px)',
          boxShadow: `0 4px 12px ${alpha(config.color, 0.18)}`,
        },
      }}
    >
      <Box display="flex" alignItems="flex-start" gap={1.5}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: selected ? config.color : alpha(config.color, 0.12),
            flexShrink: 0,
            transition: 'background 0.18s ease',
          }}
        >
          <Icon sx={{ fontSize: 20, color: selected ? '#fff' : config.color }} />
        </Box>
        <Box>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            color={selected ? config.color : 'text.primary'}
            sx={{ lineHeight: 1.3, mb: 0.4 }}
          >
            {config.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, display: 'block' }}>
            {config.description}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

const FormDiscountType: React.FC<{ formData?: any }> = ({ formData }) => {
  const chainId = getChainId();
  const { data: chain } = useGetOne('chains', { id: chainId || '' }, { enabled: !!chainId });

  const { field } = useController({ name: 'type', defaultValue: 'special' });

  const visibleTypes = useMemo(() => {
    let types = Object.keys(TYPE_CONFIG);
    if (!chain?.useGift) {
      types = types.filter(t => t !== 'reward');
    }
    if (formData?.customerIds) {
      types = types.filter(t => t === 'special');
    }
    return types;
  }, [chain?.useGift, formData?.customerIds]);

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Selecione o tipo de desconto
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Clique em um dos cartões abaixo para escolher como este desconto será aplicado. Cada tipo tem comportamentos e configurações diferentes.
        </Typography>
      </Box>
      <Grid container spacing={1.5} sx={{ mb: 2, width: '100%' }}>
        {visibleTypes.map((type) => (
          <Grid size={{ xs: 12, sm: 6 }} key={type}>
            <TypeCard
              value={type}
              selected={field.value === type}
              onClick={() => field.onChange(type)}
            />
          </Grid>
        ))}
      </Grid>

      <FormDataConsumer>
        {({ formData }) => (formData.type === 'special' || formData.type === 'reward' || formData.type === 'recovery') && (
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5, width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextInput
              source="description"
              label="Descrição"
              required={formData.type === 'special'}
              fullWidth
              helperText={(formData.type === 'reward' || formData.type === 'recovery') ? 'Opcional para este tipo de desconto' : undefined}
            />
            <BooleanInput
              source="pointsWhenDiscounting"
              defaultValue={true}
              label="Pontuar ao usar desconto"
              helperText="Se desabilitado, o cliente NÃO ganhará pontos ao usar este desconto"
            />
          </Paper>
        )}
      </FormDataConsumer>
    </>
  );
};

export default FormDiscountType;
